import React from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { Field, TextInput, TextArea, ImageUpload } from "../shared";

export const SettingsPanel = ({ settings, setSettings, onSave }) => {
  const tiers = settings.quantity_discounts || [];
  const setTier = (idx, patch) =>
    setSettings({ ...settings, quantity_discounts: tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)) });

  return (
    <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6 mb-8" data-testid="workshop-settings-panel">
      <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-5">PRICING & PRODUCT TEXT</h2>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <Field label="DEFAULT PRICE PER TAG (NZD)">
          <TextInput type="number" step="0.5" value={settings.base_price} onChange={(e) => setSettings({ ...settings, base_price: e.target.value })} data-testid="setting-base-price" />
        </Field>
        <Field label="MAX TEXT LENGTH">
          <TextInput type="number" value={settings.max_text_length} onChange={(e) => setSettings({ ...settings, max_text_length: e.target.value })} data-testid="setting-max-text" />
        </Field>
        <Field label="MAX QUANTITY">
          <TextInput type="number" value={settings.max_quantity} onChange={(e) => setSettings({ ...settings, max_quantity: e.target.value })} data-testid="setting-max-qty" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        {["15cm", "20cm", "25cm"].map((size) => (
          <Field key={size} label={`ETCHERY PLANT STAKE ${size.toUpperCase()} (NZD)`}>
            <TextInput
              type="number" step="0.5"
              value={settings.stake_prices?.[size] ?? ""}
              onChange={(e) => setSettings({ ...settings, stake_prices: { ...(settings.stake_prices || {}), [size]: e.target.value } })}
              data-testid={`setting-stake-price-${size}`}
            />
          </Field>
        ))}
      </div>

      <div className="mb-5" data-testid="quantity-discounts">
        <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-1">QUANTITY DISCOUNTS</p>
        <p className="text-xs text-plum-deep/50 mb-3">Automatic percentage off single-tag orders when the tag count reaches a tier (bundles excluded).</p>
        <div className="space-y-2">
          {tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2 bg-cream rounded-2xl p-2.5 flex-wrap" data-testid={`discount-tier-${i}`}>
              <TextInput type="number" min={2} value={t.min_qty} onChange={(e) => setTier(i, { min_qty: e.target.value })} data-testid={`discount-min-${i}`} className="w-20 !px-3" />
              <span className="text-xs text-plum-deep/50 uppercase tracking-[0.1em]">+ tags →</span>
              <TextInput type="number" min={1} max={90} value={t.percent} onChange={(e) => setTier(i, { percent: e.target.value })} data-testid={`discount-percent-${i}`} className="w-20 !px-3" />
              <span className="text-xs text-plum-deep/50">% off</span>
              <button onClick={() => setSettings({ ...settings, quantity_discounts: tiers.filter((_, j) => j !== i) })} className="text-plum-deep/40 hover:text-red-600" data-testid={`discount-delete-${i}`} aria-label="Delete discount tier">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => setSettings({ ...settings, quantity_discounts: [...tiers, { min_qty: 10, percent: 10 }] })}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-plum text-cream px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold hover:bg-magenta"
          data-testid="add-discount-tier"
        >
          <Plus className="w-3.5 h-3.5" /> ADD DISCOUNT TIER
        </button>
      </div>

      <Field label="PRODUCT DESCRIPTION (SHOWN ON WORKSHOP PAGE)">
        <TextArea rows={2} value={settings.product_text || ""} onChange={(e) => setSettings({ ...settings, product_text: e.target.value })} data-testid="setting-product-text" />
      </Field>
      <div className="mt-4">
        <Field label="PREVIEW IMAGE">
          <ImageUpload value={settings.preview_image} onChange={(url) => setSettings({ ...settings, preview_image: url })} testid="setting-preview-image" />
        </Field>
      </div>
      <button onClick={onSave} className="mt-5 inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum" data-testid="save-workshop-settings">
        <Save className="w-4 h-4" /> SAVE SETTINGS
      </button>
    </div>
  );
};
